import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const IMAGES = [
    {
        src: "/danielle/family_church.webp",
        alt: "Rebecca, her partner, and Danielle together inside a historic church.",
        caption: "N° 01 — Family, together.",
        cls: "col-span-12 sm:col-span-6 aspect-[4/3]",
        offset: "",
        preserveColor: true,
        contain: true,
    },
    {
        src: "/danielle/rebecca_danielle_close.png",
        alt: "Rebecca and Danielle, cheek to cheek. A quiet moment together.",
        caption: "N° 02 — Cheek to cheek.",
        cls: "col-span-12 sm:col-span-6 aspect-[4/3]",
        offset: "",
        preserveColor: true,
        contain: true,
    },
    {
        src: "/danielle/danielle_and_ritzy.webp",
        alt: "Danielle and her dachshund Ritzy, 2019.",
        caption: "N° 03 — Danielle & Ritzy, 2019.",
        cls: "col-span-12 sm:col-span-6 aspect-[3/4]",
        offset: "sm:mt-4",
        preserveColor: true,
        contain: true,
    },
    {
        src: "/danielle/family_deck.webp",
        alt: "Danielle with her mother Rebecca and family on the back deck.",
        caption: "N° 04 — Family, on the deck.",
        cls: "col-span-12 sm:col-span-6 aspect-[3/4]",
        offset: "sm:mt-4",
        preserveColor: true,
        contain: true,
    },
    {
        src: "/danielle/painting_latypova.jpg",
        alt: "Oil painting portrait of Danielle as a child, holding her teddy bear. Painted by Sasha Latypova.",
        caption: "N° 05 — Painting by Sasha Latypova.",
        cls: "col-span-12 sm:col-span-8 sm:col-start-3 aspect-[4/5]",
        offset: "sm:mt-8",
        preserveColor: true,
        contain: true,
    },
];

function Frame({ src, alt, caption, cls, offset, index, preserveColor, objectPos, contain }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    // Reduce parallax for `contain` frames — they don't need overflow room
    const parallaxRange = contain ? "2%" : "3%";
    const y = useTransform(
        scrollYProgress,
        [0, 1],
        [index % 2 === 0 ? parallaxRange : `-${parallaxRange}`, index % 2 === 0 ? `-${parallaxRange}` : parallaxRange]
    );
    return (
        <motion.figure
            ref={ref}
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className={`${cls} ${offset}`}
            data-testid={`gallery-frame-${index}`}
        >
            <div className={`relative w-full h-full overflow-hidden border border-neutral-300 ${contain ? "bg-neutral-100" : ""}`}>
                <motion.img
                    style={{ y, objectPosition: objectPos || "50% 50%" }}
                    src={src}
                    alt={alt}
                    className={`${preserveColor ? "" : "editorial-img "}absolute inset-0 w-full ${contain ? "h-full object-contain" : "h-[106%] object-cover"}`}
                />
            </div>
            <figcaption className="mt-3 font-sans text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                {caption}
            </figcaption>
        </motion.figure>
    );
}

export default function Gallery() {
    return (
        <section
            data-testid="gallery-section"
            className="relative w-full py-24 sm:py-32 bg-[color:var(--paper)]"
            id="gallery"
        >
            <div className="max-w-[92rem] mx-auto px-6 sm:px-12 lg:px-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                    className="grid grid-cols-12 mb-16 sm:mb-24"
                >
                    <div className="col-span-12 sm:col-span-3 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                        Chapter V — The Album
                    </div>
                    <div className="col-span-12 sm:col-span-9 mt-3 sm:mt-0">
                        <h2 className="font-serif text-5xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight text-neutral-950">
                            Danielle,{" "}
                            <span className="italic font-light text-neutral-500">
                                in her own light.
                            </span>
                        </h2>
                        <p className="mt-6 max-w-xl font-serif italic text-lg sm:text-xl text-neutral-700">
                            Family photographs of Danielle — before the hospital,
                            before the silence.
                        </p>
                    </div>
                </motion.div>

                <div className="grid grid-cols-12 gap-3 sm:gap-5">
                    {IMAGES.map((img, i) => (
                        <Frame key={i} index={i} {...img} />
                    ))}
                </div>
            </div>
        </section>
    );
}
